
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { UserForm } from '../UserForm';
import { PasswordUpdateForm } from '../PasswordUpdateForm';
import { EmailUpdateForm } from '../EmailUpdateForm';
import { Edit, Key, Mail } from 'lucide-react';
import { ProfileWithOrganization } from './useUserData';
import { Organization } from '@/types/flight';
import { Profile } from '@/types/auth';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface UserListItemProps {
  profile: ProfileWithOrganization;
  organizations: Organization[];
  isDialogOpen: boolean;
  selectedUserId: string | null;
  onEdit: (profile: ProfileWithOrganization) => void;
  onDialogChange: (open: boolean) => void;
  onSubmit: () => void;
}

export function UserListItem({
  profile,
  organizations,
  isDialogOpen,
  selectedUserId,
  onEdit,
  onDialogChange,
  onSubmit
}: UserListItemProps) {
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  
  // Check if this user is an organization user (not an admin)
  const isOrganizationUser = profile.role === 'organization' || profile.role === 'organization_user';
  
  // Fetch user email when email dialog opens
  const handleEmailDialogOpen = async () => {
    try {
      const { data, error } = await supabase.auth.admin.getUserById(profile.id);
      if (error) throw error;
      setUserEmail(data.user.email || '');
      setEmailDialogOpen(true);
    } catch (error) {
      console.error('Error fetching user email:', error);
      // Fallback: try to get from auth.users if admin API fails
      const { data: userData } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', profile.id)
        .single();
      
      if (userData) {
        // Get email from session or context if available
        setUserEmail('');
        setEmailDialogOpen(true);
      }
    }
  };
  
  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
      case 'administrator':
        return 'destructive';
      case 'organization':
      case 'organization_user':
        return 'default';
      default:
        return 'secondary';
    }
  };

  // Convert ProfileWithOrganization to Profile for UserForm compatibility
  const convertToProfile = (profile: ProfileWithOrganization | null): Profile | null => {
    if (!profile) return null;
    
    return {
      id: profile.id,
      first_name: profile.first_name,
      last_name: profile.last_name,
      role: profile.role,
      organization_id: profile.organization_id,
      gender: profile.gender,
      contact_number1: profile.contact_number1,
      contact_number2: profile.contact_number2,
      is_active: profile.is_active,
    };
  };

  return (
    <div className="flex items-center justify-between p-4 border rounded-lg">
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <div>
            <p className="font-medium">
              {profile.first_name} {profile.last_name}
            </p>
            <p className="text-sm text-muted-foreground">
              ID: {profile.id}
            </p>
          </div>
          <Badge variant={getRoleBadgeVariant(profile.role)}>
            {profile.role}
          </Badge>
          {!profile.is_active && (
            <Badge variant="outline">Inactive</Badge>
          )}
        </div>
        {profile.organization && (
          <p className="text-sm text-muted-foreground mt-1">
            Organization: {profile.organization.name}
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <Dialog open={isDialogOpen && selectedUserId === profile.id} onOpenChange={onDialogChange}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onEdit(profile)}
            >
              <Edit className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            <UserForm 
              user={convertToProfile(selectedUserId === profile.id ? profile : null)} 
              organizations={organizations}
              onSubmit={onSubmit}
            />
          </DialogContent>
        </Dialog>

        {/* Only show password update button for organization users, not for admins */}
        {isOrganizationUser && (
          <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setPasswordDialogOpen(true)}
                title="Update organization user password"
              >
                <Key className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Update Password</DialogTitle>
              </DialogHeader>
              <PasswordUpdateForm
                userId={profile.id}
                userName={`${profile.first_name} ${profile.last_name}`}
                onSubmit={() => {
                  setPasswordDialogOpen(false);
                  onSubmit();
                }}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Only show email update button for organization users, not for admins */}
        {isOrganizationUser && (
          <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleEmailDialogOpen}
                title="Update organization user email"
              >
                <Mail className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Update Email</DialogTitle>
              </DialogHeader>
              <EmailUpdateForm
                userId={profile.id}
                userName={`${profile.first_name} ${profile.last_name}`}
                currentEmail={userEmail}
                onSubmit={() => {
                  setEmailDialogOpen(false);
                  onSubmit();
                }}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
